import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Upload } from 'lucide-react';
import { Input, Button, Space, Card, Table } from 'antd';
import { fetchVendors, addVendor, updateVendor, bulkAddVendors } from '../utils/api';
import { isViewer } from '../utils/auth';

export default function Vendors() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', gstin: '', address: '' });
  const [bulkText, setBulkText] = useState('');

  const load = async (params = {}) => {
    setLoading(true);
    try {
      const data = await fetchVendors(params);
      setVendors(data.vendors || []);
      setPagination(data.pagination || {});
    } catch (e) {
      alert(e.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setFormData({ name: '', email: '', phone: '', gstin: '', address: '' });
    setShowForm(true);
  };
  const openEdit = (v) => {
    setEditingId(v._id);
    setFormData({ name: v.name || '', email: v.email || '', phone: v.phone || '', gstin: v.gstin || '', address: v.address || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.name.trim()) { alert('Name is required'); return; }
      if (editingId) {
        const updated = await updateVendor(editingId, formData);
        setVendors(prev => prev.map(v => v._id === editingId ? updated : v));
      } else {
        const created = await addVendor(formData);
        setVendors(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch (e) {
      alert(e.message || 'Failed to save vendor');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div className="page-title">
          <h1>Vendors</h1>
          <p>Manage vendor directory and details</p>
        </div>
        <div className='custom-search' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <Input
            prefix={<Search size={16} />}
            placeholder="Search vendors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => load({ search })}
            allowClear
            style={{ width: 280 }}
          />
          <Space>
            <Button icon={<Upload size={16} />} onClick={() => setShowBulk(true)}>Import CSV</Button>
            {!isViewer() && (
          <Button type="primary" icon={<Plus size={16} />} onClick={openAdd}>Add Vendor</Button>
          )}
          </Space>
        </div>
      </div>

      <Card title={`Vendors (${pagination.totalItems || vendors.length} total)`}>
        <Table
          dataSource={vendors}
          rowKey="_id"
          loading={loading}
          pagination={false}
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
              render: (text, record) => (
                <a
                  onClick={() => navigate(`/vendor/${record._id}`)}
                  style={{ fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}
                >
                  {text}
                </a>
              ),
            },
            {
              title: 'GSTIN',
              dataIndex: 'gstin',
              key: 'gstin',
              render: (text) => text || '-',
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
              render: (text) => text || '-',
            },
            {
              title: 'Phone',
              dataIndex: 'phone',
              key: 'phone',
              render: (text) => text || '-',
            },
          ]}
        />
      </Card>

      {showForm && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {editingId ? 'Edit Vendor' : 'Add New Vendor'}
                </h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowForm(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <form id="vendor-form" onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0px' }}>
                  <div className="form-group">
                    <label>Name</label>
                    <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <Input value={formData.gstin} onChange={e => setFormData({ ...formData, gstin: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <Input.TextArea rows={3} value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" form="vendor-form" className="btn btn-primary">{editingId ? 'Update Vendor' : 'Add Vendor'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Bulk Import Vendors</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowBulk(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <div className="form-group">
                <label>Paste CSV (headers: name,email,phone,gstin,address)</label>
                <Input.TextArea rows={10} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={`name,email,phone,gstin,address\nAcme Pvt Ltd,ap@example.com,9876543210,29ABCDE1234F1Z5,12 MG Road Bangalore`} />
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Tip: You can also import by selecting a CSV file.
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <input type="file" accept=".csv,text/csv" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const text = await file.text();
                  setBulkText(text);
                }} />
              </div>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', width: '100%' }}>
                <button type="button" className="btn" onClick={() => {
                  // Download template CSV
                  const content = 'name,email,phone,gstin,address\n';
                  const blob = new Blob([content], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'vendors_template.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}>Download Template</button>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowBulk(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={async () => {
                    try {
                      // Parse CSV
                      const lines = bulkText.split(/\r?\n/).filter(Boolean);
                      if (lines.length === 0) { alert('Paste CSV content or choose a file'); return; }
                      let startIdx = 0;
                      const header = lines[0].toLowerCase();
                      if (header.includes('name') && header.includes('email')) startIdx = 1;
                      const rows = lines.slice(startIdx).map(line => {
                        const cells = line.split(',');
                        const [name, email, phone, gstin, address] = cells.map(c => c?.trim());
                        return { name, email, phone, gstin, address };
                      }).filter(r => r.name);
                      if (rows.length === 0) { alert('No valid rows found'); return; }
                      const res = await bulkAddVendors(rows);
                      setVendors(prev => [...res.vendors, ...prev]);
                      alert(`Imported ${res.insertedCount} vendors`);
                      setShowBulk(false);
                    } catch (err) {
                      alert(err.message || 'Failed to import vendors');
                    }
                  }}>Import</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


